import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Clock3, RotateCcw, UserRoundX, Users } from 'lucide-react'
import { Button } from '@agms/ui'
import { ticketApi, ticketTypeApi } from '../lib/api'
import type { TicketPriority, TicketSourceChannel, TicketStatsFilters, TicketStatsPerson, TicketStatsResponse } from '../lib/api'
import { apiErrorMessage, apiErrorStatus } from '../hooks/useErrorHandler'
import { PageHeader, StatCard, SurfaceCard } from '../components/ui'

const SOURCE_OPTIONS: Array<TicketSourceChannel | 'inbox'> = ['phone', 'email', 'internal', 'inbox']

export const TICKET_STATS_FORBIDDEN_MESSAGE = "You don't have access to ticket stats."

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—'
  const minutes = Math.round(Math.abs(seconds) / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours}h`
  return `${Math.round(hours / 24)}d`
}

function formatRate(rate: number | null): string {
  if (rate === null) return '—'
  return `${Math.round(rate * 100)}%`
}

function weekLabel(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number)
  return new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1)).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

function rangeTotal(points: Array<{ hits: number; eligible: number }>): { hits: number; eligible: number; rate: number | null } {
  const hits = points.reduce((sum, point) => sum + point.hits, 0)
  const eligible = points.reduce((sum, point) => sum + point.eligible, 0)
  return { hits, eligible, rate: eligible === 0 ? null : hits / eligible }
}

export function TicketStatsPage() {
  const queryClient = useQueryClient()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [priority, setPriority] = useState<TicketPriority | ''>('')
  const [ticketTypeId, setTicketTypeId] = useState('')
  const [source, setSource] = useState<TicketSourceChannel | 'inbox' | ''>('')
  const [staleDays, setStaleDays] = useState('')
  const [settingError, setSettingError] = useState<string | null>(null)

  const filters: TicketStatsFilters = {
    from: from || undefined,
    to: to || undefined,
    priority: priority || undefined,
    ticket_type_id: ticketTypeId || undefined,
    source: source || undefined,
  }

  const stats = useQuery({
    queryKey: ['ticket-stats', filters],
    queryFn: () => ticketApi.stats(filters),
    staleTime: 30_000,
  })

  const types = useQuery({
    queryKey: ['ticket-types', 'stats-filter'],
    queryFn: () => ticketTypeApi.list(),
  })

  const saveDays = useMutation({
    mutationFn: (days: number) => ticketApi.updateStaleDays(days),
    onSuccess: async () => {
      setSettingError(null)
      await queryClient.invalidateQueries({ queryKey: ['ticket-stats'] })
    },
    onError: (error) => {
      setSettingError(apiErrorMessage(error, 'Could not save the stale-ticket setting.'))
    },
  })

  const data = stats.data
  const forbidden = stats.isError && apiErrorStatus(stats.error) === 403
  const viewer = data?.people.find((person) => person.is_viewer) ?? data?.people[0]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ticket stats"
        subtitle="How open tickets are doing."
        action={<Link to="/tickets" className="text-sm font-medium text-blue-700 hover:text-blue-900">Back to tickets</Link>}
      />

      {stats.isLoading ? (
        <p className="text-sm text-gray-500" aria-live="polite">Loading accountability...</p>
      ) : null}
      {forbidden ? (
        <p className="text-sm text-rose-700" role="alert">{TICKET_STATS_FORBIDDEN_MESSAGE}</p>
      ) : null}
      {stats.isError && !forbidden ? (
        <p className="text-sm text-rose-700" role="alert">Could not load ticket stats.</p>
      ) : null}

      {data ? (
        <>
          <section aria-label="Needs attention now" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <Link to={data.links.past_sla} className="block rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-500">
              <StatCard label="Past SLA" value={data.attention.past_sla} icon={AlertTriangle} iconColor="rose" context="Open or in progress" />
            </Link>
            <Link to={data.links.due_within_24_hours} className="block rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-500">
              <StatCard label="Due within 24 hours" value={data.attention.due_within_24_hours} icon={Clock3} iconColor="amber" context="Not already past due" />
            </Link>
            <Link to={data.links.unassigned_open} className="block rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-500">
              <StatCard label="Open, no assignee" value={data.attention.unassigned_open} icon={UserRoundX} iconColor="purple" context="Status is open" />
            </Link>
            <Link to={data.links.stale} className="block rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-500">
              <StatCard label="Stale" value={data.attention.stale} icon={Clock3} iconColor="teal" context={`No event in ${data.stale_after_days}+ days`} />
            </Link>
            <Link to={data.links.reopened} className="block rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-500">
              <StatCard label="Reopened" value={data.attention.reopened} icon={RotateCcw} iconColor="blue" context="Since tracking began" />
            </Link>
          </section>

          <SurfaceCard padding="p-4">
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(event) => {
                event.preventDefault()
                const days = Number(staleDays || data.stale_after_days)
                saveDays.mutate(days)
              }}
            >
              <label className="text-sm font-medium text-gray-700">
                Stale after (days)
                <input
                  type="number"
                  min={1}
                  max={90}
                  className="mt-1 block w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={staleDays || String(data.stale_after_days)}
                  onChange={(event) => setStaleDays(event.target.value)}
                  disabled={!data.can_edit_stale_after_days}
                />
              </label>
              {data.can_edit_stale_after_days ? (
                <Button type="submit" disabled={saveDays.isPending}>Save</Button>
              ) : (
                <p className="text-sm text-gray-500">Management sets this threshold.</p>
              )}
              {settingError ? <p className="text-sm text-rose-700" role="alert">{settingError}</p> : null}
            </form>
            <p className="mt-2 text-xs text-gray-500">{data.notes.stale} {data.notes.reopened}</p>
          </SurfaceCard>

          {viewer ? <MyQueue person={viewer} /> : null}

          <SurfaceCard>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-500" aria-hidden="true" />
              <h2 className="text-base font-semibold text-ink">{data.scope === 'team' ? 'By person' : 'My queue'}</h2>
            </div>
            <p className="mt-1 text-sm text-gray-500">{data.notes.people} {data.notes.medians} {data.notes.sla_hit_rate}</p>
            <PeopleTable people={data.people} />
          </SurfaceCard>

          <SurfaceCard padding="p-4">
            <h2 className="text-base font-semibold text-ink">Trends</h2>
            <p className="mt-1 text-sm text-gray-500">
              {weekLabel(data.filters.from)} – {weekLabel(data.filters.to)}, {data.business_timezone}. These filters do not change the attention cards or the all-time medians and SLA rate.
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <label className="text-sm font-medium text-gray-700">
                From
                <input type="date" className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm" value={from} onChange={(event) => setFrom(event.target.value)} />
              </label>
              <label className="text-sm font-medium text-gray-700">
                To
                <input type="date" className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm" value={to} onChange={(event) => setTo(event.target.value)} />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Priority
                <select className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm" value={priority} onChange={(event) => setPriority(event.target.value as TicketPriority | '')}>
                  <option value="">All</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700">
                Type
                <select className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm" value={ticketTypeId} onChange={(event) => setTicketTypeId(event.target.value)}>
                  <option value="">All</option>
                  {(types.data ?? []).map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700">
                Source
                <select className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm" value={source} onChange={(event) => setSource(event.target.value as TicketSourceChannel | 'inbox' | '')}>
                  <option value="">All</option>
                  {SOURCE_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
            </div>
          </SurfaceCard>

          <div className="grid gap-4 lg:grid-cols-2">
            <SurfaceCard>
              <h2 className="text-base font-semibold text-ink">Opened vs resolved</h2>
              <WeekBars rows={data.opened_vs_resolved} />
            </SurfaceCard>
            <SurfaceCard>
              <h2 className="text-base font-semibold text-ink">SLA hit rate</h2>
              <p className="mt-1 text-xs text-gray-500">{data.notes.sla_hit_rate}</p>
              <SlaTable points={data.sla_over_time.by_week} />
            </SurfaceCard>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <SurfaceCard>
              <h2 className="text-base font-semibold text-ink">SLA by priority</h2>
              <BreakdownTable
                rows={data.sla_over_time.by_priority.map((row) => ({
                  label: row.priority,
                  ...rangeTotal(row.by_week),
                }))}
              />
            </SurfaceCard>
            <SurfaceCard>
              <h2 className="text-base font-semibold text-ink">SLA by type</h2>
              <BreakdownTable
                rows={data.sla_over_time.by_type.map((row) => ({
                  label: row.name,
                  ...rangeTotal(row.by_week),
                }))}
              />
            </SurfaceCard>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <SurfaceCard>
              <h2 className="text-base font-semibold text-ink">Top ticket types</h2>
              <CountList rows={data.top_types.map((row) => ({ label: row.name, count: row.count }))} empty="No tickets opened in this range." />
            </SurfaceCard>
            <SurfaceCard>
              <h2 className="text-base font-semibold text-ink">Merchants with the most tickets</h2>
              <CountList
                rows={data.top_merchants.map((row) => ({
                  label: row.dba_name,
                  count: row.count,
                  href: `/merchants/${row.merchant_id}`,
                }))}
                empty="No merchant tickets opened in this range."
              />
            </SurfaceCard>
          </div>
        </>
      ) : null}
    </div>
  )
}

function MyQueue({ person }: { person: TicketStatsPerson }) {
  return (
    <SurfaceCard>
      <h2 className="text-base font-semibold text-ink">My queue</h2>
      <p className="mt-1 text-sm text-gray-500">{person.user.name} · {person.user.role.replaceAll('_', ' ')}</p>
      <dl className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <QueueStat label="Open" value={String(person.open)} href={person.links.open} />
        <QueueStat label="Overdue" value={String(person.overdue)} href={person.links.overdue} />
        <QueueStat label="Resolved this week" value={String(person.resolved_this_week)} />
        <QueueStat label="Median resolve (all time)" value={formatDuration(person.median_resolve_seconds)} detail={person.resolve_samples ? `${person.resolve_samples} tickets` : 'No resolved tickets'} />
        <QueueStat label="Median first response (all time)" value={formatDuration(person.median_first_response_seconds)} detail={person.first_response_samples ? `${person.first_response_samples} tickets` : 'No first response yet'} />
        <QueueStat label="SLA hit rate (all time)" value={formatRate(person.sla_hit_rate)} detail={person.sla_eligible ? `${person.sla_hits} of ${person.sla_eligible}` : 'No closed SLA tickets'} />
      </dl>
    </SurfaceCard>
  )
}

function QueueStat({ label, value, detail, href }: { label: string; value: string; detail?: string; href?: string }) {
  const body = (
    <>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold text-slate-950">{value}</dd>
      {detail ? <p className="mt-1 text-xs text-slate-500">{detail}</p> : null}
    </>
  )
  if (!href) {
    return <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">{body}</div>
  }
  return (
    <Link to={href} className="rounded-xl border border-slate-200 bg-slate-50 p-3 hover:border-brand-300 hover:bg-white">
      {body}
    </Link>
  )
}

function PeopleTable({ people }: { people: TicketStatsPerson[] }) {
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Person</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Open</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Overdue</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Resolved this week</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Median resolve (all time)</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Median first response (all time)</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">SLA hit rate (all time)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {people.map((person) => (
            <tr key={person.user.id} className={person.is_viewer ? 'bg-blue-50/70' : undefined}>
              <td className="px-3 py-2 font-medium text-gray-900">
                {person.user.name}
                <div className="text-xs font-normal text-gray-500">{person.user.role.replaceAll('_', ' ')}</div>
              </td>
              <td className="px-3 py-2">
                <Link to={person.links.open} className="font-medium text-blue-700 hover:text-blue-900">{person.open}</Link>
              </td>
              <td className="px-3 py-2">
                <Link to={person.links.overdue} className="font-medium text-blue-700 hover:text-blue-900">{person.overdue}</Link>
              </td>
              <td className="px-3 py-2 text-gray-700">{person.resolved_this_week}</td>
              <td className="px-3 py-2 text-gray-700">{formatDuration(person.median_resolve_seconds)}</td>
              <td className="px-3 py-2 text-gray-700">{formatDuration(person.median_first_response_seconds)}</td>
              <td className="px-3 py-2 text-gray-700">{formatRate(person.sla_hit_rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function WeekBars({ rows }: { rows: TicketStatsResponse['opened_vs_resolved'] }) {
  const max = Math.max(1, ...rows.map((row) => Math.max(row.opened, row.resolved)))
  const visible = rows.filter((row) => row.opened > 0 || row.resolved > 0)
  const shown = visible.length > 0 ? visible : rows.slice(-4)

  if (shown.every((row) => row.opened === 0 && row.resolved === 0)) {
    return <p className="mt-4 text-sm text-gray-500">No tickets opened or resolved in this range.</p>
  }

  return (
    <ul className="mt-4 space-y-3">
      {shown.map((row) => (
        <li key={row.week_start}>
          <div className="mb-1 text-xs font-medium text-gray-500">Week of {weekLabel(row.week_start)}</div>
          <Bar label={`Opened ${row.opened}`} value={row.opened} max={max} className="bg-brand-500" />
          <Bar label={`Resolved ${row.resolved}`} value={row.resolved} max={max} className="bg-emerald-500" />
        </li>
      ))}
    </ul>
  )
}

function Bar({ label, value, max, className }: { label: string; value: number; max: number; className: string }) {
  return (
    <div className="mt-1 flex items-center gap-2">
      <div className="h-2 flex-1 rounded-full bg-slate-100">
        <div className={`h-2 rounded-full ${className}`} style={{ width: `${Math.max(value === 0 ? 0 : 4, (value / max) * 100)}%` }} />
      </div>
      <span className="w-24 text-xs text-gray-600">{label}</span>
    </div>
  )
}

function SlaTable({ points }: { points: TicketStatsResponse['sla_over_time']['by_week'] }) {
  const rows = points.filter((point) => point.eligible > 0)
  if (rows.length === 0) {
    return <p className="mt-4 text-sm text-gray-500">No resolved tickets with an SLA in this range.</p>
  }
  return (
    <table className="mt-4 min-w-full text-sm">
      <thead>
        <tr className="text-left text-xs font-medium text-gray-500">
          <th className="py-1 pr-3">Week</th>
          <th className="py-1 pr-3">Hit rate</th>
          <th className="py-1">Hits</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((point) => (
          <tr key={point.week_start} className="border-t border-gray-100">
            <td className="py-1.5 pr-3">{weekLabel(point.week_start)}</td>
            <td className="py-1.5 pr-3">{formatRate(point.rate)}</td>
            <td className="py-1.5">{point.hits} of {point.eligible}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function BreakdownTable({ rows }: { rows: Array<{ label: string; hits: number; eligible: number; rate: number | null }> }) {
  if (rows.length === 0) {
    return <p className="mt-4 text-sm text-gray-500">Nothing resolved with an SLA in this range.</p>
  }
  return (
    <table className="mt-4 min-w-full text-sm">
      <thead>
        <tr className="text-left text-xs font-medium text-gray-500">
          <th className="py-1 pr-3">Group</th>
          <th className="py-1 pr-3">Hit rate</th>
          <th className="py-1">Hits</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label} className="border-t border-gray-100">
            <td className="py-1.5 pr-3 capitalize">{row.label.replaceAll('_', ' ')}</td>
            <td className="py-1.5 pr-3">{formatRate(row.rate)}</td>
            <td className="py-1.5">{row.hits} of {row.eligible}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function CountList({ rows, empty }: { rows: Array<{ label: string; count: number; href?: string }>; empty: string }) {
  if (rows.length === 0) {
    return <p className="mt-4 text-sm text-gray-500">{empty}</p>
  }
  return (
    <ul className="mt-4 divide-y divide-gray-100 text-sm">
      {rows.map((row) => (
        <li key={row.label} className="flex items-center justify-between py-2">
          {row.href ? (
            <Link to={row.href} className="font-medium text-blue-700 hover:text-blue-900">{row.label}</Link>
          ) : (
            <span className="text-gray-800">{row.label}</span>
          )}
          <span className="font-semibold text-gray-900">{row.count}</span>
        </li>
      ))}
    </ul>
  )
}
